// Snippets editor — cloud port of the desktop snippets library editor.
// Third tab of the Query surface (Query | Browse | Snippets): the SnippetsBar
// on the Query tab inserts and quick-saves; this page is the full library —
// browse/filter, create, edit name/description/content, delete.
//
// Writes are gated by ManageDashboards (same as the snippets API); other
// roles get a read-only library with copy.

import { useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { SubTabs } from '@/components/SubTabs';
import {
  createSnippet,
  deleteSnippet,
  patchSnippet,
  useSnippets,
  type SnippetDef,
} from '@/lib/useSnippets';
import { canManageDashboards, useMe } from '@/lib/useMe';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';

export function SnippetsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const me = useMe();
  const canEdit = canManageDashboards(me.data);
  const qc = useQueryClient();
  const snippets = useSnippets();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // Draft state: null = nothing open; id "" = new snippet.
  const [draft, setDraft] = useState<{
    id: string;
    name: string;
    description: string;
    content: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const list = snippets.data?.snippets ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q),
    );
  }, [list, filter]);

  const selected = list.find((s) => s.id === selectedId) ?? null;

  const refresh = () => void qc.invalidateQueries({ queryKey: ['snippets'] });

  const openFor = (s: SnippetDef) => {
    setSelectedId(s.id);
    setDraft({
      id: s.id,
      name: s.name,
      description: s.description,
      content: s.content,
    });
    setError(null);
    setCopied(false);
  };

  const openNew = () => {
    setSelectedId(null);
    setDraft({ id: '', name: '', description: '', content: '' });
    setError(null);
    setCopied(false);
  };

  async function handleSave() {
    if (!token || !draft) return;
    const name = draft.name.trim();
    const content = draft.content.trim();
    if (!name || !content) {
      setError('A snippet needs at least a name and content.');
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      name,
      content,
      description: draft.description.trim(),
    };
    const r = draft.id
      ? await patchSnippet(draft.id, body, token)
      : await createSnippet(body, token);
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    refresh();
    if (!draft.id && r.data && 'id' in r.data) {
      setSelectedId(r.data.id);
      setDraft({ ...draft, id: r.data.id });
    }
  }

  async function handleDelete() {
    if (!token || !draft?.id) return;
    if (!window.confirm(`Delete snippet "${draft.name}"? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    const r = await deleteSnippet(draft.id, token);
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    refresh();
    setDraft(null);
    setSelectedId(null);
  }

  async function handleCopy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Could not copy to clipboard.');
    }
  }

  return (
    <div className="container mx-auto py-8">
      <SubTabs
        tabs={[
          { to: '/query', label: 'Query' },
          { to: '/browse', label: 'Browse', page: 'browse' },
          { to: '/snippets', label: 'Snippets', page: 'snippets' },
        ]}
      />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Snippets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The shared query library. Snippets saved here (or from the Query tab&apos;s snippet
            bar) are available to everyone on this instance.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={openNew}
            className={buttonVariants({ variant: 'primary', size: 'md' })}
          >
            New snippet
          </button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,1fr)_2fr]">
        {/* Library list */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-3 py-2">
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name or description…"
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="max-h-[65vh] overflow-y-auto p-2">
            {snippets.isLoading && (
              <p className="px-2 py-1 text-sm text-tres-text-secondary">Loading snippets…</p>
            )}
            {snippets.error && (
              <p className="px-2 py-1 text-sm text-tres-error">
                {(snippets.error as Error).message}
              </p>
            )}
            {snippets.data && filtered.length === 0 && (
              <p className="px-2 py-1 text-sm text-tres-text-secondary">
                {list.length === 0
                  ? 'No snippets yet. Save one here or from the Query tab.'
                  : 'No snippets match the filter.'}
              </p>
            )}
            {filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openFor(s)}
                className={cn(
                  'block w-full rounded px-2 py-1.5 text-left text-sm',
                  selectedId === s.id
                    ? 'bg-tres-submenu-bg font-medium text-tres-text-primary'
                    : 'text-tres-text-primary hover:bg-muted',
                )}
                title={s.description || s.name}
              >
                <span className="block truncate">{s.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Editor / viewer */}
        <div>
          {!draft ? (
            <div className="rounded-lg border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
              {canEdit
                ? 'Select a snippet to edit it, or create a new one.'
                : 'Select a snippet to view it.'}
            </div>
          ) : (
            <div className="space-y-3 rounded-md border border-border bg-card p-4">
              <label className="block">
                <span className="text-xs font-medium text-tres-text-secondary">Name</span>
                <input
                  type="text"
                  value={draft.name}
                  readOnly={!canEdit}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-tres-text-secondary">Description</span>
                <input
                  type="text"
                  value={draft.description}
                  readOnly={!canEdit}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-tres-text-secondary">Content</span>
                <textarea
                  value={draft.content}
                  readOnly={!canEdit}
                  onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  rows={14}
                  spellCheck={false}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm"
                />
              </label>
              {error && <p className="text-sm text-tres-error">{error}</p>}
              <div className="flex flex-wrap items-center gap-2">
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={busy}
                    className={buttonVariants({ variant: 'primary', size: 'lg' })}
                  >
                    {busy ? 'Saving…' : draft.id ? 'Save' : 'Create'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void handleCopy(draft.content)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
                >
                  {copied ? 'Copied' : 'Copy content'}
                </button>
                {canEdit && draft.id && (
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={busy}
                    className={cn(buttonVariants({ variant: 'danger', size: 'lg' }), 'ml-auto')}
                  >
                    Delete
                  </button>
                )}
              </div>
              {selected && (
                <p className="text-xs text-tres-text-secondary">
                  Updated {selected.updated_at}
                  {selected.created_by ? ` · created by ${selected.created_by}` : ''}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
